import React, { useContext, useEffect, useMemo, useState } from "react";
import { FaGraduationCap, FaUniversity, FaGlobe, FaBook, FaArrowRight, FaChartLine } from "react-icons/fa";
import { Link } from "react-router-dom";
import { countryImages } from "../../utils/countryImages";
import OpportunityGrid from "../OpportunityGrid/OpportunityGrid";
import SEOMetadata from "../SEOMetadata/SEOMetadata";
import SubscribeEmail from "../SubscribeEmail/SubscribeEmail";
import { OpportunityService } from "../../services/OpportunityService";
import { useQuery } from "../../hooks/useQuery";
import { AuthContext } from "../../context/AuthContext";
import { useMutations } from "../../hooks/useMutations";
import useAnalytics from '../../hooks/useAnalytics';
import phdscannerBrand from "../../assets/phdscanner_brand.webp";
import WetsusBanner from '../WetsusBanner/WetsusBanner';

function replaceSpacesWithHyphens(str) {
  return str.replace(/\s+/g, '-');
}

export default function Home() {
  const [finalSavedOpportunities, setSavedOpportunities] = useState(new Set());
  const { userDetails: user } = useContext(AuthContext);
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
  const { trackEvent } = useAnalytics(GA_MEASUREMENT_ID);

  const {
    data: featuredOpportunities = [],
    error: featuredOpperror,
    isLoading: featuredOpportunitiesLoading,
  } = useQuery(
    ["opportunity countries count", "featured opportunities"],
    () => OpportunityService.getAllOpportunities({
      category: "featured",
      limit: 9,
    }),
  );


  const {
    data: data = [],
    error: oppbycntryError,
    isLoading: loadingSavedOppByCntry,
  } = useQuery(
    ["getOpportunityCountriesCount"],
    OpportunityService.getOpportunityCountriesCount,
  );

  // Conditionally call getSavedOpportunities only if user is logged in
  const {
    data: savedOpportunities = [],
    error: savedOppError,
    isLoading: loadingSavedOpp,
    isSuccess: savedOppSuccess,
  } = useQuery(
    ["getSavedOpportunities", user?.id], // Include user ID in the query key
    () => OpportunityService.getSavedOpportunities({ userId: user.id }), // Pass user ID to the service
    {
      enabled: !!user, // Only run this query if user is logged in
    }
  );


  // Convert the fetched data to a Set
  useEffect(() => {
    // Safely extract the array from savedOpportunities object
    const opportunitiesArray = Array.isArray(savedOpportunities?.savedOpportunities)
      ? savedOpportunities.savedOpportunities
      : [];

    if (savedOppSuccess && opportunitiesArray.length > 0) {
      setSavedOpportunities(new Set(opportunitiesArray));
    }
  }, [savedOpportunities, savedOppSuccess]);


  const { mutateAsync: createSaveOpportunity } = useMutations(
    OpportunityService.createSavedOpportunity
  );

  const { mutateAsync: deleteSaveOpportunity } = useMutations(
    OpportunityService.deleteSavedOpportunities
  );

  const opportunitiesByCountry = useMemo(
    () =>
      data?.map?.((item) => ({
        ...item,
        image_url: countryImages[item.Country] || "", // Map country to image URL
      })) || [],
    [data],
  );

  // Toggle save/unsave opportunities
  const toggleSave = (opportunityId) => {
    if (!user) {
      return; // Redirect to login or handle user not logged in
    }

    setSavedOpportunities((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(opportunityId)) {
        newSet.delete(opportunityId);
      } else {
        newSet.add(opportunityId);
      }
      return newSet;
    });
  };

  const {
    data: universitiesData = {},
    error: universitiesError,
    isLoading: loadingUniversities,
  } = useQuery(
    ["getOpportunityUniversitiesCounts"],
    OpportunityService.getOpportunityUniversitiesCounts,
  );

  const {
    data: disciplineData = [],
    error: disciplineError,
    isLoading: loadingDiscipline,
  } = useQuery(
    ["getOpportunityDisciplineCount"],
    OpportunityService.getOpportunityDisciplineCount
  );

  // Create a memoized version of universities data
  const universitiesByCountry = useMemo(() => {
    return Object.entries(universitiesData).map(([country, universities]) => ({
      country,
      universities: universities.map(uni => ({
        ...uni,
        image_url: countryImages[country] || "", // Map country to image URL
      }))
    }));
  }, [universitiesData]);

  const {
    data: latestOpportunitiesRaw = {},
    error: latestOpportunitiesError,
    isLoading: latestOpportunitiesLoading,
  } = useQuery(
    ["latestOpportunities"],
    () => OpportunityService.getAllOpportunities({ limit: 12, sort: 'latest' })
  );
  const latestOpportunities = Array.isArray(latestOpportunitiesRaw?.data)
    ? latestOpportunitiesRaw.data
    : Array.isArray(latestOpportunitiesRaw)
      ? latestOpportunitiesRaw
      : [];

  const {
    data: mostViewedOpportunitiesRaw = {},
    error: mostViewedOpportunitiesError,
    isLoading: mostViewedOpportunitiesLoading,
  } = useQuery(
    ["mostViewedOpportunities"],
    () => OpportunityService.getMostViewedOpportunities()
  );
  const mostViewedOpportunities = Array.isArray(mostViewedOpportunitiesRaw?.data)
    ? mostViewedOpportunitiesRaw.data
    : Array.isArray(mostViewedOpportunitiesRaw)
      ? mostViewedOpportunitiesRaw
      : [];

  const handleToggleSave = async (opportunityId) => {
    if (!user) {
      return; // Redirect to login or handle user not logged in
    }

    try {
      if (finalSavedOpportunities.has(opportunityId)) {
        await deleteSaveOpportunity({
          user_id: user.id,
          opportunity_id: opportunityId,
        });
        toggleSave(opportunityId);
      } else {
        trackEvent({
          category: 'Button',
          action: 'Click',
          label: 'User Opportunity Save'
        });

        await createSaveOpportunity({
          user_id: user.id,
          opportunity_id: opportunityId,
        });
        toggleSave(opportunityId);
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  return (
    <div >
      <WetsusBanner />
      <SEOMetadata
  title={`3000+ PhD Positions Worldwide | Find & Apply to Doctoral Programs | PhDScanner`}
  description={`Discover 3000+ fully-funded PhD positions, research fellowships, and doctoral programs at top universities worldwide. Start your academic career today.`}
  keywords={[
    // Global search terms
    'PhD positions',
    'PhD programs',
    'doctoral studies',
    'research opportunities',
    'PhD vacancies',
    'phd jobs', 
    'university jobs',
    'jobs', 
    'jobs education technology',  
    'research in education jobs',
    
    // Funding-specific
    'fully funded PhD',
    'PhD with stipend',
    `PhD scholarships ${new Date().getFullYear()}`,
    `PhD grants ${new Date().getFullYear()}`,
    'paid doctoral programs',
    
    // Geographic terms
    'international PhD',
    'global PhD positions',
    'worldwide doctoral programs',
    'study abroad PhD',
    
    // Discipline-specific
    'STEM PhD',
    'humanities PhD',
    'social sciences PhD',
    'engineering doctoral programs',
    'biology PhD positions',
    
    // University-related
    'university PhD openings',
    'research university positions',
    'postgraduate vacancies',
    'academic research careers',
    
    // Action-oriented
    'how to find PhD',
    'apply for doctoral programs',
    'PhD application process',
    
    // Branded
    'PhDScanner database',
    'PhD search platform',
    'find PhD opportunities'
  ].join(', ')}
  imageUrl="https://www.phdscanner.com/phdscanner_logo.webp"
  url="https://www.phdscanner.com/"
/>

      <section className="w-full px-2 md:px-8 border-t border-[#DB4F40]">
        <picture>
          <source
            media="(max-width: 768px)"
            srcSet={phdscannerBrand}
            type="image/webp"
          />
          <source
            media="(min-width: 769px)"
            srcSet={phdscannerBrand}
            type="image/webp"
          />
          <img
            src={phdscannerBrand}
            alt="PhD Opportunities Worldwide"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </picture>
      </section>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#FFBEB9] to-[#FFD1CE] border-t border-[#DB4F40]/20 py-24">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center space-y-8 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1B222A] leading-tight">
              <span className="block mb-2 bg-gradient-to-r from-[#DB4F40] to-[#C64537] bg-clip-text text-transparent">
                Discover Your Next
              </span>
              <span className="block bg-gradient-to-r from-[#C64537] to-[#DB4F40] bg-clip-text text-transparent">
                Academic Adventure
              </span>
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-700 max-w-3xl leading-relaxed">
              Your gateway to top-tier academic roles and groundbreaking PhD opportunities worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link
                to="/phd-vacancies/standard"
                className="relative inline-flex items-center justify-center px-8 py-4 font-semibold text-white 
                         bg-gradient-to-br from-[#DB4F40] to-[#C64537] rounded-full shadow-lg
                         transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
                onClick={() => {
                  trackEvent({
                    category: 'Button',
                    action: 'Click',
                    label: 'View All Opportunities from Home'
                  });
                }}
              >
                View All Opportunities
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 w-full max-w-6xl">
              {[
                {
                  icon: FaUniversity,
                  number: "100+",
                  label: "Universities",
                  color: "text-blue-500"
                },
                {
                  icon: FaGraduationCap,
                  number: "2K+",
                  label: "PhD Positions",
                  color: "text-green-500"
                },
                {
                  icon: FaGlobe,
                  number: "15+",
                  label: "Countries",
                  color: "text-purple-500"
                },
                {
                  icon: FaChartLine,
                  number: "50K+",
                  label: "Active Researchers",
                  color: "text-indigo-500"
                }
              ].map((stat, index) => (
                <div
                  key={index}
                  className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 text-center 
                           shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <stat.icon className={`mx-auto text-4xl mb-3 ${stat.color}`} />
                  <div className="text-3xl font-bold text-[#DB4F40] mb-2">{stat.number}</div>
                  <div className="text-gray-700 text-base">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="border-t border-[#F3D1CC]" />

      {/* Latest Opportunities Section */}
      <section className="py-24 bg-gradient-to-b from-[#FFF6F3] to-[#FFEEED] border-t border-[#F3D1CC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-[#1B222A] mb-4">
              Latest PhD Opportunities
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Browse the most recently added PhD positions from top universities worldwide.
            </p>
            <div className="flex justify-center mt-8">
              <Link
                to="/phd-vacancies/standard"
                className="relative inline-flex items-center justify-center px-8 py-4 font-semibold text-white 
                         bg-gradient-to-br from-[#DB4F40] to-[#C64537] rounded-full shadow-lg
                         transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
                onClick={() => {
                  trackEvent({
                    category: 'Button',
                    action: 'Click',
                    label: 'View All Opportunities from Home'
                  });
                }}
              >
                View All Opportunities
              </Link>
            </div>
          </div>
          {latestOpportunitiesLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#DB4F40] border-t-transparent" />
            </div>
          ) : (
            <>
              <OpportunityGrid
                opportunities={latestOpportunities}
                user={user}
                savedOpportunities={finalSavedOpportunities}
                handleToggleSave={handleToggleSave}
                gridCols="grid-cols-1"
                gap="gap-8"
              />
            </>
          )}
        </div>
      </section>

      {/* Divider */}
      <div className="border-t border-[#F3D1CC]" />

      {/* Most Viewed Opportunities Section */}
      <section className="py-24 bg-gradient-to-b from-[#FFF6F3] to-[#FFEEED] border-t border-[#F3D1CC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-[#1B222A] mb-4">
              Top 12 Most Viewed Opportunities
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Explore the most popular PhD opportunities based on researcher interest.
            </p>
            <div className="flex justify-center mt-8">
              <Link
                to="/phd-vacancies/standard"
                className="relative inline-flex items-center justify-center px-8 py-4 font-semibold text-white 
                         bg-gradient-to-br from-[#DB4F40] to-[#C64537] rounded-full shadow-lg
                         transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
                onClick={() => {
                  trackEvent({
                    category: 'Button',
                    action: 'Click',
                    label: 'View All Opportunities from Home'
                  });
                }}
              >
                View All Opportunities
              </Link>
            </div>
          </div>
          {mostViewedOpportunitiesLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#DB4F40] border-t-transparent" />
            </div>
          ) : (
            <OpportunityGrid
              opportunities={mostViewedOpportunities}
              user={user}
              savedOpportunities={finalSavedOpportunities}
              handleToggleSave={handleToggleSave}
              gridCols="grid-cols-1"
              gap="gap-8"
            />
          )}
        </div>
      </section>

      {/* Divider */}
      <div className="border-t border-[#F3D1CC]" />

      {/* Countries Section */}
      <section className="py-24 bg-white border-t border-[#F3D1CC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-[#1B222A] mb-4">
              Explore PhD Opportunities Worldwide
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Discover prestigious academic roles and groundbreaking research opportunities across the globe.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {opportunitiesByCountry.slice(0, 9).map((opportunity, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#FFF6F3] to-[#FFD1CE] rounded-2xl overflow-hidden shadow-lg 
                   hover:shadow-xl transition-all duration-300 group relative"
              >
                <Link
                  to={`/phd-vacancies/standard/${replaceSpacesWithHyphens(opportunity.Country)}`}
                  onClick={(e) => {
                    e.preventDefault();
                    trackEvent({
                      category: 'Opportunity',
                      action: 'View',
                      label: 'Opportunity by country'
                    });
                    window.location.href = `/phd-vacancies/standard/${replaceSpacesWithHyphens(opportunity.Country)}`;
                  }}
                >
                  <div className="relative aspect-video">
                    <img
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      src={opportunity.image_url}
                      alt={opportunity.Country}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>
                  <h3 className="text-2xl font-bold text-white text-center mb-4 mt-4">
                    {/* Change country name color to brand red */}
                    <span className="text-[#DB4F40]">{opportunity.Country}</span>
                  </h3>
                  <div className="flex items-center justify-center">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full px-6 py-3 flex items-center">
                      <FaGraduationCap className="text-[#DB4F40] text-xl mr-2" />
                      <span className="text-gray-800 font-medium">
                        {opportunity.Count} Opportunities
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SubscribeEmail />
    </div>
  );
}
